'use client'

import { type Dispatch, type SetStateAction, useState, useEffect, useRef } from 'react'
import type {
	ProductFragment,
	ProductSubCategoryFragment,
} from '@/lib/types/datocms/graphql/generated'
import ProductDetails from './ProductDetails'
import Icon from '@/components/svg/icon'
import Swoosh from '@/components/svg/Swoosh'
import clsx from 'clsx'
import CategorySubMenu from './CategorySubMenu'

export default function NavSubMenu({
	setIsMegaMenuOpen,
	setIsImageModalOpen,
	isImageModalOpen,
	subCategories,
	setShowMobileMenu,
	products,
}: Readonly<{
	setIsMegaMenuOpen: Dispatch<SetStateAction<boolean>>
	setIsImageModalOpen: Dispatch<SetStateAction<boolean>>
	isImageModalOpen: boolean
	subCategories: ProductSubCategoryFragment[]
	setShowMobileMenu: Dispatch<SetStateAction<boolean>>
	products: ProductFragment[]
}>) {
	const [activeAccordion, setActiveAccordion] = useState<string | null>(null)
	const [activeProduct, setActiveProduct] = useState<string | null>(null)

	// Remove duplicates from products array using Set
	const uniqueProducts = Array.from(
		new Map(products.map((product) => [product.id, product])).values()
	)

	// Get products only for the active accordion/subcategory
	const productsBySubCategory = uniqueProducts.filter(
		(product) => product.category?.id === activeAccordion
	)
	const selectedProductDetails = productsBySubCategory.find((item) => item.id === activeProduct)

	const initialLoad = useRef(true)

	// Handle initial accordion state
	useEffect(() => {
		if (subCategories.length > 0 && initialLoad.current && !activeAccordion) {
			const firstSubCategoryWithProducts = subCategories.find((subCategory) => {
				const subcategoryProducts = uniqueProducts.filter(
					(product) => product.category?.id === subCategory.id
				)
				return subcategoryProducts.length > 0
			})

			if (firstSubCategoryWithProducts) {
				setActiveAccordion(firstSubCategoryWithProducts.id as string)
				const firstProduct = uniqueProducts.find(
					(product) => product.category?.id === firstSubCategoryWithProducts.id
				)
				if (firstProduct && window.innerWidth > 1024) {
					setActiveProduct(firstProduct.id)
				}
			}
			initialLoad.current = false
		}
	}, [subCategories, activeAccordion, uniqueProducts])

	const handleAccordionClick = (id: string) => {
		if (activeAccordion === id) {
			setActiveAccordion(null)
			setActiveProduct(null)
		} else {
			setActiveAccordion(id)
			// Only set first product if there isn't already an active product
			if (!activeProduct) {
				const firstProductInCategory = uniqueProducts.find((product) => product.category?.id === id)
				if (firstProductInCategory && window.innerWidth > 1024) {
					setActiveProduct(firstProductInCategory.id)
				}
			}
		}
	}

	return (
		// Tab Panel (Accordion Sidebar + Product Detail)
		subCategories && (
			<div className="flex h-full">
				{/* Accordion Sidebar */}
				{/* Mobile: Slide 2 / Part 1 */}
				<div className="relative max-lg:w-full lg:w-[350px] shrink-0 bg-white-frosted lg:pt-8 pb-20 overflow-scroll">
					<div className="absolute bottom-0 max-md:w-[100vw] w-[350px] z-0 overflow-hidden">
						<Swoosh
							className="origin-left translate-y-1/4 translate-x-6 scale-75"
							fill="#E6EDF7"
							opacity="1"
						/>
					</div>

					{subCategories.map((subCategory) => {
						// Filter unique products for each subcategory
						const subcategoryProducts = uniqueProducts.filter(
							(product) => product.category?.id === subCategory.id
						)

						return (
							<CategorySubMenu
								key={subCategory.id}
								handleAccordionClick={handleAccordionClick}
								activeAccordion={activeAccordion || ''}
								subCategory={subCategory}
								setActiveProduct={setActiveProduct}
								activeProduct={activeProduct}
								products={subcategoryProducts}
							/>
						)
					})}
				</div>
				{/* Product Details Panel */}
				<div
					className={clsx(
						'@container w-full h-full overflow-y-auto',
						'max-lg:fixed max-lg:inset-0 max-lg:z-10 max-lg:bg-white',
						'max-lg:transition-transform max-lg:duration-500 max-lg:ease-s',
						selectedProductDetails ? 'max-lg:translate-x-0' : 'max-lg:translate-x-full'
					)}
				>
					{/* Mobile Back Button */}
					<button
						className="lg:hidden flex gap-2 py-5 px-3 w-full border-b hover:bg-gray-light/40 transition-colors duration-300"
						onClick={() => setActiveProduct('')}
					>
						<Icon icon="Arrow" className="rotate-180" />
						<span className="text-lg font-semibold text-blue-dark/60">Back</span>
					</button>

					{/* Product Details */}
					{selectedProductDetails && (
						<ProductDetails
							product={selectedProductDetails}
							setIsMegaMenuOpen={setIsMegaMenuOpen}
							setIsImageModalOpen={setIsImageModalOpen}
							isImageModalOpen={isImageModalOpen}
							setShowMobileMenu={setShowMobileMenu}
						/>
					)}
				</div>
			</div>
		)
	)
}
